import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon } from 'lucide-react';
import AccountSection from './AccountSection';
import SettingsDrawer from './SettingsDrawer';
import EditEmptyState from './EditEmptyState';
import { useEditModeSettings } from '../../../../hooks/useEditModeSettings';
import {
  useAccountCategories,
  persistAccounts,
  SECTION_ORDER,
  SECTION_LABELS,
  type AccountEditRow,
  type AccountCategorySlug,
  type AccountCategory,
} from '../../../../hooks/useAccountCategories';

const OPEN_SETTINGS_EVENT = 'novala:open-edit-settings';

export function EditModeHeader() {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-3.5 md:px-8 flex justify-between items-start gap-5">
      <div className="flex-1">
        <button
          type="button"
          onClick={() => navigate('/accounting/chart-of-accounts')}
          className="inline-flex items-center gap-0.5 text-[12.5px] font-semibold bg-transparent border-none p-0 cursor-pointer hover:underline mb-2"
          style={{ color: '#0F6E56' }}
        >
          <span className="text-[15px] leading-none mr-0.5">‹</span> Back to chart of accounts
        </button>
        <div className="text-[13px] text-ink3 mb-1 font-medium">
          <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting')}>Accounting</span>
          <span className="text-ink3 mx-1.5">/</span>
          <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting/chart-of-accounts')}>Chart of accounts</span>
        </div>
        <h1 className="text-xl md:text-3xl font-extrabold m-0 -tracking-[0.5px] text-ink leading-none">Chart of accounts</h1>
      </div>
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT))}
          className="h-10 px-[18px] rounded-lg bg-white font-semibold text-[13px] inline-flex items-center gap-2 cursor-pointer"
          style={{ color: '#0E1A1A', border: '1.5px solid #0E1A1A' }}
        >
          <SettingsIcon className="w-3.5 h-3.5" /> Settings
        </button>
        <button
          type="button"
          onClick={() => navigate('/accounting/chart-of-accounts')}
          className="h-10 px-[18px] rounded-lg text-white font-semibold text-[13px] cursor-pointer border-none"
          style={{ background: '#0E1A1A' }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

export default function EditModePage() {
  const { data: initialCategories } = useAccountCategories();
  const { settings, toggleColumn, togglePreference, resetToDefaults } = useEditModeSettings();

  const [accounts, setAccounts] = useState<AccountEditRow[]>(() =>
    initialCategories.flatMap((c) => c.accounts)
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    persistAccounts(accounts);
  }, [accounts]);

  useEffect(() => {
    const handler = () => setSettingsOpen(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, handler as EventListener);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler as EventListener);
  }, []);

  const categories = useMemo<AccountCategory[]>(
    () =>
      SECTION_ORDER.map((slug) => ({
        slug,
        label: SECTION_LABELS[slug],
        accounts: accounts.filter((a) => a.categorySlug === slug),
      })),
    [accounts]
  );

  const handleUpdate = useCallback((id: string, patch: Partial<AccountEditRow>) => {
    setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  const handleAddAccount = useCallback((afterId: string, categorySlug: AccountCategorySlug) => {
    setAccounts((prev) => {
      const idx = prev.findIndex((a) => a.id === afterId);
      const newAccount: AccountEditRow = {
        id: `new-${Date.now()}`,
        name: '',
        accountType: '',
        detailType: '',
        locked: false,
        categorySlug,
      };
      if (idx < 0) return [...prev, newAccount];
      return [...prev.slice(0, idx + 1), newAccount, ...prev.slice(idx + 1)];
    });
  }, []);

  const handleAddSubAccount = useCallback((parentId: string, categorySlug: AccountCategorySlug) => {
    setAccounts((prev) => {
      const idx = prev.findIndex((a) => a.id === parentId);
      const newSub: AccountEditRow = {
        id: `new-${Date.now()}`,
        name: '',
        accountType: '',
        detailType: '',
        locked: false,
        parentId,
        categorySlug,
      };
      if (idx < 0) return [...prev, newSub];
      return [...prev.slice(0, idx + 1), newSub, ...prev.slice(idx + 1)];
    });
  }, []);

  const handleDelete = useCallback((id: string) => {
    if (settings.preferences.confirmBeforeDelete) {
      const ok = window.confirm('Delete this account and any of its sub accounts?');
      if (!ok) return;
    }
    setAccounts((prev) => prev.filter((a) => a.id !== id && a.parentId !== id));
  }, [settings.preferences.confirmBeforeDelete]);

  const isEmpty = accounts.length === 0;

  return (
    <div style={{ maxWidth: '100%', margin: 0, padding: '0 32px 90px' }}>
      {isEmpty ? (
        <EditEmptyState />
      ) : (
        <div className="pt-4">
          {categories.map((cat, idx) => (
            <AccountSection
              key={cat.slug}
              category={cat}
              defaultOpen={settings.preferences.collapseOnLoad ? false : idx === 0}
              settings={settings}
              onUpdate={handleUpdate}
              onAddAccount={handleAddAccount}
              onAddSubAccount={handleAddSubAccount}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        toggleColumn={toggleColumn}
        togglePreference={togglePreference}
        resetToDefaults={resetToDefaults}
      />
    </div>
  );
}